import { ArrowBackIosNewRounded, ArrowForwardIosRounded } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useParams } from 'react-router-dom';

import {
  ActionsWrapper,
  ComponentsWrapper,
  EditContent,
  Layout,
  PageWrapper,
  PaginationWrapper,
  PreviewsAndPageWrapper,
  PreviewsWrapper,
  RootWrapper,
} from '@/admins/SmartReports/components/EditSmartReport/layout';
import { useGetReport } from '@/admins/SmartReports/hooks/useGetReports';
import { STATIC_END_PAGES } from '../constants';
import { SmartReportLayouts } from '../Layouts';
import type { LayoutDefinition } from '../Layouts/types';
import { EditPage } from './EditPage';
import { Actions, ComponentsDrawerButton } from './EditSmartReportActions';
import { EditSmartReportProvider, useEditSmartReport } from './EditSmartReportContext';
import { EditSmartReportHeader } from './EditSmartReportHeader';
import { Previews } from './Previews';
import { SideBar } from './SideBar';
import { SmartReportDrawer } from './SmartReportDrawer';

const EditSmartReportPagination = () => {
  const { pages, selectedPage, nextPage, previousPage } = useEditSmartReport();

  const currentPage = selectedPage?.number || 1;
  const numPages = (pages?.length || 1) + STATIC_END_PAGES.length;

  return (
    <PaginationWrapper>
      <IconButton onClick={previousPage}>
        <ArrowBackIosNewRounded />
      </IconButton>
      <Typography component='span'>
        {currentPage}/{numPages}
      </Typography>
      <IconButton onClick={nextPage}>
        <ArrowForwardIosRounded />
      </IconButton>
    </PaginationWrapper>
  );
};

const Components = () => {
  const { isDrawerOpen, closeDrawer } = useEditSmartReport();

  return (
    <SmartReportDrawer.Root isOpen={isDrawerOpen} onClose={closeDrawer} useBackdrop={false}>
      <ComponentsDrawerButton isDrawerOpen={isDrawerOpen} />

      <SmartReportDrawer.Content>
        <ComponentsWrapper>
          <SideBar />
        </ComponentsWrapper>
      </SmartReportDrawer.Content>
    </SmartReportDrawer.Root>
  );
};

const EditSmartReportComponent = () => {
  const { id } = useParams<{ id: string }>();
  const { report, loading, error } = useGetReport(id);
  const { setReport, setSelectedLayout } = useEditSmartReport();

  useEffect(() => {
    if (report) {
      setReport(report);
      const layoutId = report.smartReportVersions?.[0]?.pages?.[0]?.layout;
      const foundLayout = SmartReportLayouts.find(layout => layout?.id === layoutId);
      setSelectedLayout((foundLayout ?? null) as LayoutDefinition | null);
    }
  }, [report, setReport, setSelectedLayout]);

  if (loading) {
    return (
      <RootWrapper sx={{ display: 'grid', placeContent: 'center' }}>
        <CircularProgress color='inherit' size={20} />
      </RootWrapper>
    );
  }

  if (error) return null;

  if (!report || !id) {
    return <div>Report not found</div>;
  }

  return (
    <RootWrapper>
      <Layout>
        <EditContent>
          <Box component='header'>
            <EditSmartReportHeader />
          </Box>

          <PreviewsAndPageWrapper>
            <PreviewsWrapper>
              <Previews />
            </PreviewsWrapper>
            <PageWrapper>
              <EditPage />
            </PageWrapper>
            <ActionsWrapper>
              <EditSmartReportPagination />
              <Actions />
            </ActionsWrapper>
          </PreviewsAndPageWrapper>
        </EditContent>

        <Components />
      </Layout>
    </RootWrapper>
  );
};

export const EditSmartReport = () => {
  return (
    <DndProvider backend={HTML5Backend}>
      <EditSmartReportProvider>
        <EditSmartReportComponent />
      </EditSmartReportProvider>
    </DndProvider>
  );
};
